// src/services/inventoryService.js

import mockInventoryData from '../data/mockInventoryData.json';
import apiClient from '../api/apiClient';
import { getOrgId } from '../utils/orgUtils';

export const getInventoryItems = async (options = {}) => {
  try {
    const orgId = getOrgId();
    const params = new URLSearchParams();
    
    // Add pagination parameters if provided
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);
    if (options.search) params.append('search', options.search);
    if (options.sortBy) params.append('sortBy', options.sortBy);
    if (options.sortOrder) params.append('sortOrder', options.sortOrder);
    if (options.dateFrom) params.append('dateFrom', options.dateFrom);
    if (options.dateTo) params.append('dateTo', options.dateTo);
    
    const queryString = params.toString();
    const url = `/inventory/org/${orgId}${queryString ? `?${queryString}` : ''}`;
    
    const response = await apiClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching inventory:', error);
    throw error;
  }
};

export const findInventoryItemByScannerReference = (scannerReference) => {
  const item = mockInventoryData.find(
    (data) => data.scanner_reference === scannerReference
  );
  return Promise.resolve(item || null);
};

/**
 * Add a new inventory item.
 * @param {Object} data - The inventory item data.
 * @returns {Promise<void>}
 */
export const addInventoryItem = async (data) => {
  try {
    const orgId = getOrgId();
    data.org_id = orgId; // Ensure orgId is included in the payload
    const response = await apiClient.post('/inventory', data);
    return response.data;
  } catch (error) {
    console.error('Error adding inventory:', error);
    throw error;
  }
};

/**
 * Fetch inventory items by organization ID with pagination support.
 * @param {number} orgId - The ID of the organization.
 * @param {Object} options - Pagination and filtering options.
 * @returns {Promise<Object>} - A promise that resolves to paginated inventory data.
 */
export const getInventoryItemsByOrgId = async (orgId, options = {}) => {
  const params = new URLSearchParams();
  
  // Add pagination parameters if provided
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);
  if (options.search) params.append('search', options.search);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  if (options.sortOrder) params.append('sortOrder', options.sortOrder);
  if (options.dateFrom) params.append('dateFrom', options.dateFrom);
  if (options.dateTo) params.append('dateTo', options.dateTo);
  
  const queryString = params.toString();
  const url = `/inventory/org/${orgId}${queryString ? `?${queryString}` : ''}`;
  
  const response = await apiClient.get(url);
  return response.data;
};

/**
 * Update an existing inventory item.
 * @param {string} productId - The ID of the product to update.
 * @param {Object} updatedData - The updated inventory item data.
 * @returns {Promise<void>}
 */
export const updateInventoryItem = (productId, updatedData) => {
  const index = mockInventoryData.findIndex(
    (item) => item.product_id === productId
  );

  if (index !== -1) {
    const existingItem = mockInventoryData[index];

    // Update fields
    const updatedItem = {
      ...existingItem,
      ...updatedData,
      date_modified: new Date().toISOString(),
    };

    // Recalculate calculated fields
    updatedItem.price_minus_cost =
      updatedItem.last_unit_price && updatedItem.last_unit_cost
        ? parseFloat(updatedItem.last_unit_price) - parseFloat(updatedItem.last_unit_cost)
        : existingItem.price_minus_cost;

    updatedItem.total_value =
      updatedItem.quantity_on_hand && updatedItem.last_unit_cost
        ? parseInt(updatedItem.quantity_on_hand, 10) * parseFloat(updatedItem.last_unit_cost)
        : existingItem.total_value;

    // Update audit_log
    updatedItem.audit_log = existingItem.audit_log || [];
    updatedItem.audit_log.push({
      date: updatedItem.date_modified,
      action: 'Updated',
      details: 'Inventory item updated',
    });

    // Replace the item in mock data
    mockInventoryData[index] = updatedItem;

    return Promise.resolve();
  } else {
    return Promise.reject(new Error('Inventory item not found'));
  }
};

export const getInventoryItemById = (productId) => {
  const item = mockInventoryData.find((data) => data.product_id === productId);
  return Promise.resolve(item || null);
};

export const getInventoryByProductId = async (orgId, productId) => {
  if (!orgId || !productId) {
    throw new Error('Organization ID and Product ID are required');
  }
  const response = await apiClient.get(`/inventory/org/${orgId}/product/${productId}`);
  return response.data;
};

export const getInventoryMovements = async (orgId, productId) => {
  return apiClient.get(`/inventory/product/${productId}/movements?org_id=${orgId}`)
    .then(response => response.data);
};

export const updateInventory = async (orgId, productId, data) => {
  const response = await apiClient.put(`/inventory/org/${orgId}/product/${productId}`, data);
  return response.data;
};

export const getOrganizationMovements = async (orgId, limit = 20) => {
  return apiClient.get(`/inventory/org/${orgId}/movements?limit=${limit}`)
    .then(response => {
      const movements = response.data;
      
      // Create a map to store the quantity state for each product at each timestamp
      const movementMap = new Map();
      const uniqueProducts = new Set();

      // Process each movement chronologically
      movements.forEach(movement => {
        const timestamp = new Date(movement.date_of_movement).getTime();
        uniqueProducts.add(movement.product_name);
        
        // Initialize or get the map entry for this timestamp
        if (!movementMap.has(timestamp)) {
          movementMap.set(timestamp, {
            date_of_movement: timestamp,
          });
        }
        
        // Update the quantity for this product at this timestamp
        movementMap.get(timestamp)[movement.product_name] = movement.quantity;
      });

      // Convert map to array and sort by timestamp
      const timelineData = Array.from(movementMap.values())
        .sort((a, b) => a.date_of_movement - b.date_of_movement);

      return {
        timelineData,
        products: Array.from(uniqueProducts)
      };
    });
};

export const deleteMultipleInventoryItems = (inventoryIds) => {
  return apiClient.post('/inventory/bulk-delete', { inventory_ids: inventoryIds })
    .then(response => response.data);
};

export const bulkCreateInventory = (data) => {
  return apiClient.post('/inventory/bulk', data).then(response => response.data);
};